import type { ResourcesConfig } from 'aws-amplify';

import { oauthRedirectPath } from '@constants/auth.constant';

type BuildAmplifyConfigInput = {
  basePath: string;
  env: Pick<
    ImportMetaEnv,
    | 'VITE_VOICE_AGENT_COGNITO_CLIENT_ID'
    | 'VITE_VOICE_AGENT_COGNITO_DOMAIN'
    | 'VITE_VOICE_AGENT_COGNITO_USER_POOL_ID'
    | 'VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_IN'
    | 'VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_OUT'
  >;
  origin: string;
};

type LoginWith = NonNullable<
  NonNullable<NonNullable<ResourcesConfig['Auth']>['Cognito']>['loginWith']
>;

export function buildAmplifyConfig({
  basePath,
  env,
  origin,
}: BuildAmplifyConfigInput): ResourcesConfig {
  const appBasePath = basePath.replace(/\/$/, '');
  const cognitoDomain = env.VITE_VOICE_AGENT_COGNITO_DOMAIN?.replace(
    /^https?:\/\//,
    '',
  );
  const loginWith: LoginWith = { email: true };

  // Without a domain (e.g. a Superconductor Preview) the Hosted UI is off and
  // sign-in only uses Amplify signIn (SRP), which needs no callback URL.
  if (cognitoDomain) {
    loginWith.oauth = {
      domain: cognitoDomain,
      redirectSignIn: [
        env.VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_IN ||
          `${origin}${appBasePath}${oauthRedirectPath}`,
      ],
      redirectSignOut: [
        env.VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_OUT ||
          `${origin}${appBasePath || '/'}`,
      ],
      responseType: 'code',
      scopes: ['email', 'openid', 'phone', 'profile'],
    };
  }

  // User-pool tokens only. identityPoolId would make Amplify call Cognito
  // Identity GetId, and this app client is not a provider of that pool.
  return {
    Auth: {
      Cognito: {
        loginWith,
        userPoolClientId: env.VITE_VOICE_AGENT_COGNITO_CLIENT_ID,
        userPoolId: env.VITE_VOICE_AGENT_COGNITO_USER_POOL_ID,
      },
    },
  };
}

// Pass fields one by one: handing over import.meta.env would make Vite inline
// every VITE_ variable into the bundle.
export const amplifyConfig = buildAmplifyConfig({
  basePath: import.meta.env.BASE_URL,
  env: {
    VITE_VOICE_AGENT_COGNITO_CLIENT_ID: import.meta.env
      .VITE_VOICE_AGENT_COGNITO_CLIENT_ID,
    VITE_VOICE_AGENT_COGNITO_DOMAIN: import.meta.env
      .VITE_VOICE_AGENT_COGNITO_DOMAIN,
    VITE_VOICE_AGENT_COGNITO_USER_POOL_ID: import.meta.env
      .VITE_VOICE_AGENT_COGNITO_USER_POOL_ID,
    VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_IN: import.meta.env
      .VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_IN,
    VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_OUT: import.meta.env
      .VITE_VOICE_AGENT_OAUTH_REDIRECT_SIGN_OUT,
  },
  origin: globalThis.location.origin,
});
